import { Link, useLocation } from 'react-router-dom';

import { buttonVariants } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { cn } from '@/utils/helpers/cn';
import { withNextParam } from '@/utils/helpers/next-path';

/** Um destino a mais, para quem não vai criar conta agora — sempre uma rota pública. */
export interface GuestGateExtraAction {
  label: string;
  to: string;
}

/** O que o portão diz. É também o formato da copy por rota no `handle` (`RequireSession`). */
export interface GuestGateCopy {
  /** O que a conta destrava, dito como fato: "Suas notas ficam na sua conta". */
  title: string;
  /** Por que precisa de conta e o que a pessoa ganha, em uma ou duas frases. */
  description: string;
  extraAction?: GuestGateExtraAction;
}

interface GuestGateProps extends GuestGateCopy {
  /**
   * Para onde voltar depois de entrar ou criar a conta. Default: a tela atual
   * (caminho + query) — que é o que a pessoa estava tentando abrir.
   */
  next?: string;
  className?: string;
}

/**
 * "Esta tela pede uma conta" — o portão de convidado das **telas**.
 *
 * O convidado vê o nome da tela (no `PageHeader` de quem monta), por que ela
 * precisa de conta e os dois caminhos, em vez de cair num formulário de login
 * genérico sem saber o que a tela faria. Para **ações** dentro de uma tela
 * pública, o par é o `LoginRequiredSheet`.
 *
 * Unifica três desenhos da mesma coisa (auditoria de UX, §1.3): callout +
 * dois botões no Perfil, em Instituições e na aba "Para você"; texto + um botão
 * só nas vagas; e o redirecionamento seco do `RequireSession`.
 *
 * ## O `next` vai nos dois botões
 *
 * Login e cadastro resolvem `?next=` (`resolveNextPath`). Sem ele, entrar na
 * conta devolveria a pessoa ao mapa e a tela que ela queria estaria perdida —
 * que é o momento exato em que se desiste.
 *
 * São `<Link>` com `buttonVariants`, não `<Button onClick={navigate}>`: os
 * destinos são rotas, e a âncora dá "abrir em outra aba", foco pelo Tab e o
 * anúncio de link de graça (docs/03).
 */
export function GuestGate({ title, description, extraAction, next, className }: GuestGateProps) {
  const location = useLocation();
  const target = next ?? location.pathname + location.search;

  return (
    <section aria-label={title} className={cn('flex flex-col gap-4', className)}>
      <Callout variant="info" title={title}>
        {description}
      </Callout>

      <div className="flex flex-col gap-2">
        <Link to={withNextParam('/login', target)} className={buttonVariants({ fullWidth: true })}>
          Entrar na sua conta
        </Link>

        <Link
          to={withNextParam('/register', target)}
          className={buttonVariants({ variant: 'secondary', fullWidth: true })}
        >
          Criar conta
        </Link>

        {extraAction ? (
          <Link
            to={extraAction.to}
            className={buttonVariants({ variant: 'ghost', fullWidth: true })}
          >
            {extraAction.label}
          </Link>
        ) : null}
      </div>
    </section>
  );
}
